import type { Locale } from "@/content/site";

type CopyShape<T> = T extends string
  ? string
  : T extends number
    ? number
    : T extends readonly unknown[]
      ? { readonly [K in keyof T]: CopyShape<T[K]> }
      : T extends object
        ? { readonly [K in keyof T]: CopyShape<T[K]> }
        : T;

const enMarketingRouteCopy = {
  home: {
    heroSupportBullets: [
      "Choose the right workflow before building.",
      "Do not automate before risk and ownership are clear.",
      "Fix the data foundation required for useful AI.",
      "Ship with evaluation, monitoring, security review, and human review.",
      "Measure adoption, ROI, and operating impact.",
    ],
    heroFlowNodes: [
      { label: "Workflow", sub: "Select" },
      { label: "Data", sub: "Ready" },
      { label: "System", sub: "Build" },
      { label: "Impact", sub: "Measure", emphasis: true },
    ],
    proof: {
      primaryFocus: { label: "Primary focus", value: "High-value workflows and recurring decisions." },
      deliveryLabel: "Delivery",
      evidence: { label: "Evidence", value: "Four real engagements presented anonymously." },
      controls: {
        label: "Controls",
        value: "Evaluation, monitoring, human review, governance, and value measurement.",
      },
    },
    problemFailures: [
      {
        code: "01",
        label: "Work keeps piling up",
        body: "Teams repeatedly classify, reconcile, search, draft, or hand off the same work across disconnected systems.",
      },
      {
        code: "02",
        label: "Decisions stay slow",
        body: "The data exists, but thresholds, policy, ownership, and the next action still live in spreadsheets or meetings.",
      },
      {
        code: "03",
        label: "A pilot cannot be operated",
        body: "The prototype works, but live data, exceptions, review, integration, adoption, and measurement were never designed.",
      },
    ],
    methodChecks: ["Measurable workflow", "Usable data", "Human review boundary", "ROI baseline"],
    goodFit: [
      "A recurring workflow or decision with a visible operating cost.",
      "An accountable workflow owner who can support adoption.",
      "Accessible data, even when quality work is still required.",
      "An operating signal the team will baseline before the build.",
    ],
    notFit: [
      "A demo with no owner, integration, or adoption path.",
      "A scope defined only by a preferred tool or model.",
      "A guaranteed-return expectation without a baseline or defensible attribution.",
      "High-risk automation with no human authority or escalation path.",
    ],
    hero: {
      eyebrow: "Production AI systems",
      code: "VERT / HOMEPAGE",
      systemLabel: "Production operating system",
      statusLabel: "READY / 08",
      flowStamp: "SYSTEM / 01",
      statusRows: [
        ["Signal", "Measurable use case"],
        ["Control", "Human review boundary"],
        ["Decision", "Clear production path"],
      ],
    },
    problem: {
      eyebrow: "The problem",
      headline: "Start with an operating problem whose cost is already visible.",
      body: "Vert AI becomes relevant when important work is slow, manual, or inconsistent—or when a recurring decision remains difficult to execute despite the data already available.",
    },
    builds: {
      eyebrow: "What Vert AI builds",
      headline: "A redesigned workflow. A reliable system. A measurable result.",
      body: "Vert AI connects the workflow, data, rules, AI, controls, and measurement in one operating system. The intended result is a capability the team uses and can run with confidence.",
      exploreSystems: "Explore production AI systems",
      buyerSignalLabel: "Buyer signal",
      buyerSignal:
        "The right starting point is a recurring workflow where better speed, quality, prioritization, or review can be measured. Not every workflow should be automated.",
      selectedEngagementLabel: "Selected real engagement · anonymized",
      deliveredCapabilityPrefix: "Delivered capability: ",
      measurementFramePrefix: "Measurement frame: ",
      evidenceBoundary: "No numeric outcome or client identity is published without an approved evidence record.",
      capabilityIndexLabel: "Capability index",
      capabilityIndexHeadline: "Three implementation paths selected by the operating constraint.",
      valuePatternsLabel: "Value patterns",
      valuePatternsHeadline: "Value shows up when the workflow changes.",
      valuePatternsBody:
        "The best use cases are operational, measurable, and close to recurring decisions. Humans stay in control where judgment, risk, or customer impact matters.",
    },
    review: {
      eyebrow: "Specialized decision review",
      headline: "Facing a real AI funding, launch, or scale decision?",
      body: "Vert AI reviews one live initiative and identifies what is supported, what remains assumed, what could fail, and what must become true before proceeding. The specialized Review can also fit a decision-ready initiative that is not yet live.",
      facts: [
        { label: "Scope", value: "One initiative" },
        { label: "Standard timing", value: "10 business days after Evidence Readiness" },
        { label: "Standard fee", value: "C$9,500 + applicable taxes" },
      ],
      applyLabel: "Apply for the Review",
      sampleLabel: "View the Sample",
    },
    operatingSystem: {
      eyebrow: "Scope to production",
      headline: "Choose. Prove. Deploy. Operate.",
      body: "Each phase reduces a specific uncertainty: workflow value, strength of evidence, deployment reliability, or the team’s ability to operate the system.",
      compareEngagements: "Compare engagement paths",
      gatesLabel: "Production gates",
      methodLabel: "Delivery method",
      methodHeadline: "A direct path from scoping to a system in operation.",
      methodBody: "The work starts with the operating problem, not the model. Each step reduces delivery risk before production.",
      humanMarker: "HUMAN /",
    },
    trust: {
      eyebrow: "Trust and governance",
      headline: "Production AI needs more than a model.",
      body: "The system has to be observable, governed, measurable, and usable by the teams who depend on it. Humans stay in control where judgment, risk, or customer impact matters.",
    },
    founder: {
      eyebrow: "Founder credibility",
      headline: "Senior production decisions remain founder-led.",
      body: "Guillaume leads scoping and key technical decisions. His professional experience includes designing and shipping production AI, ML, and data systems in environments including Louis Vuitton, Canada Goose, Lowe’s, and Home Hardware. These organizations are founder-experience references, not presented as Vert AI clients.",
      aboutLink: "Review founder-led delivery",
      insightsLink: "Read implementation analysis",
    },
    industries: {
      eyebrow: "Who it is for",
      headline: "Production AI for operations-intensive businesses.",
      body: "Vert AI has published domain depth in retail and ecommerce, consumer goods, and franchise or multi-location operations. The same production patterns can apply to manufacturing, distribution, and professional and B2B services when the workflow, data, controls, and accountable owner are clear.",
      exploreLink: "Explore industry contexts",
      publishedLabel: "Detailed sector guides",
      publishedTitle: "Where Vert AI has published domain depth",
      publishedTypeLabel: "Detailed guide",
      applicableLabel: "Applicable production patterns",
      applicableTitle: "Where the same production patterns apply",
      applicableBody: "These contexts link to the relevant solution pattern. Fit depends on a defined workflow, usable data, explicit controls, and an accountable owner.",
      applicableTypeLabel: "Solution pattern",
    },
    fit: {
      eyebrow: "Engagement fit",
      headline: "A strong engagement starts with a specific operating problem.",
      body: "These boundaries keep scoping focused on opportunities that can become adopted, measurable operating capabilities.",
      goodFitLabel: "Good fit",
      notFitLabel: "Not a fit",
    },
    faqHeadline: "Direct answers for serious buyers.",
    final: {
      eyebrow: "Next decision",
      headline: "Bring a production AI opportunity that deserves serious scoping.",
      body: "Bring a workflow, a data problem, or a recurring decision. We will map the value, risks, ownership, and path to production, including whether not to build yet.",
    },
  },
  services: {
    breadcrumbs: { home: "Home", current: "Services" },
    schemaServiceType:
      "Production AI Systems, Data Platforms for AI, Decision Intelligence, and specialized AI Verification Review",
    flowStamp: "SERVICES / 03",
    choiceSituationLabel: "Buying situation",
    choiceEngagementLabel: "Engagement",
    review: {
      eyebrow: "Specialized decision review",
      headline: "A material AI decision deserves an evidence-backed review.",
      facts: [
        { label: "Scope", value: "One initiative" },
        { label: "Standard timing", value: "10 business days" },
        { label: "Timing starts", value: "After Evidence Readiness" },
        { label: "Standard fee", value: "C$9,500 + tax" },
      ],
      applyLabel: "Apply for the Review",
      sampleLabel: "View the Sample",
    },
  },
  industries: {
    breadcrumbs: { home: "Home", current: "Industries" },
    heroLabel: "Industries / Operating decisions",
    heroTitle: "Industry provides the context. The workflow determines the system.",
    heroSubtitle:
      "Vert AI has its deepest published context in retail and ecommerce, consumer goods, and franchise or multi-location operations. The same production patterns can apply to manufacturing, distribution, and professional and B2B services when the workflow, data, exception path, and accountable owners are clear.",
    primaryCta: "Discuss a production AI opportunity",
    secondaryCta: "Review engagement formats",
    registryLabel: "Detailed sector guides",
    registryTitle: "Where Vert AI has published domain depth",
    registrySubtitle:
      "Each page defines the operating friction, minimum data, human controls, baseline measures, and conditions that can make a first project premature.",
    guideTypeLabel: "Detailed guide",
    expansionLabel: "Applicable production patterns",
    expansionTitle: "Where the same production patterns apply",
    expansionSubtitle:
      "Manufacturing, distribution, and professional-service teams often face the same planning, document, knowledge, and exception patterns. These links lead to the relevant system pattern rather than a separate sector offer.",
    patternTypeLabel: "Solution pattern",
    fitStatement:
      "Industry is not the qualifier. A strong fit is a high-value workflow with an accountable owner, usable data, clear risk boundaries, and an operating result that can be measured.",
    fitCta: "Discuss a specific workflow",
    adjacentLabel: "Continue the evaluation",
    solutionsLink: "Compare solution patterns",
    servicesLink: "Review engagement formats",
    ctaHeadline: "Bring a recurring decision, not an AI wish list.",
    ctaDesc:
      "Bring the current workflow, source systems, decision owner, and an observable baseline. Vert AI will assess whether the first investment should be data work, decision support, or a governed automation workflow.",
    registryWord: "Registry",
    heroMetaSuffix: "detailed sector guides · 2 additional operating contexts · Decision-first scoping",
    flowStamp: "INDUSTRIES / SYSTEMS",
    endLabel: "END /",
    flowNodes: [
      { label: "Decision", sub: "Owner" },
      { label: "Data", sub: "Signals" },
      { label: "System", sub: "Controls" },
      { label: "Operation", sub: "Baseline" },
    ],
  },
  solutions: {
    breadcrumbs: { home: "Home", current: "Solutions" },
    flowNodes: [
      { label: "Problem", sub: "Scope" },
      { label: "Solution", sub: "Select" },
      { label: "Engagement", sub: "Build" },
      { label: "Production", sub: "Operate" },
    ],
    heroPatternsSuffix: "solution patterns",
    heroCapabilitiesSuffix: "supporting capabilities",
    registryLabel: "Registry",
    engagementPrefix: "Via ·",
    engagementLabels: {
      productionAiSystems: "Production AI Systems",
      decisionIntelligence: "Decision Intelligence",
    },
    openPatternLabel: "Review solution pattern",
    capabilityLabel: "Capability",
    bridgeLinks: [
      { href: "/services", label: "Explore production AI systems" },
      { href: "/industries", label: "Review industry fit" },
    ],
    endLabel: "END /",
  },
  insights: {
    breadcrumbs: { home: "Home", current: "Insights" },
    flowNodes: [
      { label: "Question", sub: "Framing" },
      { label: "Framework", sub: "Method" },
      { label: "Reference", sub: "Decision" },
    ],
    heroMetaSuffix: "articles · Frameworks · Operating references",
    flowStamp: "INSIGHTS / GUIDES",
    featuredLabel: "Featured",
    readingPathsLabel: "Reading paths",
    readingPathsTitle: "Guides organized around the decision at hand.",
    readingPathsIntro:
      "Each path connects strategy, data, and production controls to a specific leadership question.",
    readingPaths: [
      {
        title: "Move from pilot to production",
        description:
          "Evaluation, governance, human authority, and controls required before an AI system carries real work.",
      },
      {
        title: "Select and fund the right initiative",
        description:
          "Decision frameworks for selecting a workflow, baselining value, comparing delivery options, and testing vendor evidence.",
      },
      {
        title: "Build the data and decision foundation",
        description:
          "Data architecture, governed definitions, and operating mechanisms that turn information into decisions.",
      },
    ],
    continueLabel: "Continue evaluating",
    adjacentLinks: [
      { href: "/solutions", label: "Review production AI solution patterns" },
      { href: "/contact", label: "Discuss a production AI opportunity" },
    ],
    endLabel: "END /",
  },
  about: {
    breadcrumbs: { home: "Home", current: "About" },
    founderJobTitle: "Founder, Vert AI",
    positioningSignalsAria: "Vert AI positioning signals",
    positioningSignalsLabel: "What this page establishes",
    nextStepLabel: "Next step",
  },
  work: {
    homeBreadcrumb: "Home",
    pathLabels: { contact: "BUILD", verification: "VERIFY" },
  },
} as const;

const frMarketingRouteCopy = {
  home: {
    heroSupportBullets: [
      "Choisir le bon processus opérationnel avant de construire.",
      "Ne pas automatiser avant que le risque et la responsabilité soient clairs.",
      "Corriger la fondation de données requise pour une IA utile.",
      "Livrer avec évaluation, surveillance, sécurité et validation humaine.",
      "Mesurer l’adoption, le rendement et l’impact opérationnel.",
    ],
    heroFlowNodes: [
      { label: "Processus", sub: "Choisir" },
      { label: "Données", sub: "Préparer" },
      { label: "Système", sub: "Construire" },
      { label: "Impact", sub: "Mesurer", emphasis: true },
    ],
    proof: {
      primaryFocus: { label: "Priorité", value: "Processus à forte valeur et décisions récurrentes." },
      deliveryLabel: "Livraison",
      evidence: { label: "Preuves", value: "Quatre mandats réels présentés de façon anonymisée." },
      controls: {
        label: "Contrôle",
        value: "Évaluation, surveillance, validation humaine, gouvernance et mesure de la valeur.",
      },
    },
    problemFailures: [
      {
        code: "01",
        label: "Le travail s’accumule",
        body: "Les équipes classent, rapprochent, recherchent, rédigent ou transfèrent le même type de travail à répétition entre plusieurs systèmes.",
      },
      {
        code: "02",
        label: "Les décisions restent lentes",
        body: "Les données existent, mais les seuils, les règles, la responsabilité et la prochaine action demeurent dans des fichiers ou des réunions.",
      },
      {
        code: "03",
        label: "Un pilote ne peut pas être exploité",
        body: "Le prototype fonctionne, mais les données réelles, les exceptions, la validation, l’intégration, l’adoption et la mesure n’ont pas été conçues.",
      },
    ],
    methodChecks: ["Processus mesurable", "Données utilisables", "Validation humaine définie", "Mesure de référence"],
    goodFit: [
      "Un processus ou une décision récurrente dont le coût est déjà visible.",
      "Une personne responsable du processus et de son adoption.",
      "Des données accessibles, même si leur qualité doit être améliorée.",
      "Un indicateur opérationnel que l’équipe accepte de mesurer avant le développement.",
    ],
    notFit: [
      "Une démonstration sans propriétaire, intégration ou voie d’adoption.",
      "Un mandat défini uniquement par un outil ou un modèle à utiliser.",
      "Une attente de rendement garanti sans mesure de référence ni attribution défendable.",
      "Une automatisation à risque élevé sans autorité humaine ni mécanisme de traitement des exceptions.",
    ],
    hero: {
      eyebrow: "Systèmes d’IA en production",
      code: "VERT / ACCUEIL",
      systemLabel: "Système opérationnel",
      statusLabel: "PRÊT / 08",
      flowStamp: "SYSTÈME / 01",
      statusRows: [
        ["Signal", "Cas d’usage mesurable"],
        ["Contrôle", "Validation humaine définie"],
        ["Décision", "Chemin de production clair"],
      ],
    },
    problem: {
      eyebrow: "Le problème",
      headline: "Le bon point de départ est un problème opérationnel dont le coût est déjà visible.",
      body: "Vert AI intervient lorsqu’un processus important est lent, manuel ou incohérent, ou lorsqu’une décision récurrente reste difficile à exécuter malgré les données disponibles.",
    },
    builds: {
      eyebrow: "Ce que Vert AI construit",
      headline: "Un processus repensé. Un système fiable. Un résultat mesurable.",
      body: "Vert AI relie le processus, les données, les règles, l’IA, les contrôles et la mesure dans un même système. Le résultat attendu est une capacité que l’équipe utilise et peut exploiter avec confiance.",
      exploreSystems: "Explorer les systèmes d’IA en production",
      buyerSignalLabel: "Indicateur d’adéquation",
      buyerSignal:
        "Le bon point de départ est un processus récurrent où l’amélioration de la vitesse, de la qualité, de la priorisation ou de la validation peut être mesurée. Tous les processus ne devraient pas être automatisés.",
      selectedEngagementLabel: "Mandat réel sélectionné · anonymisé",
      deliveredCapabilityPrefix: "Capacité livrée : ",
      measurementFramePrefix: "Cadre de mesure : ",
      evidenceBoundary: "Aucun résultat chiffré ni nom de client n’est publié sans preuve approuvée.",
      capabilityIndexLabel: "Répertoire des capacités",
      capabilityIndexHeadline: "Trois parcours de mise en œuvre selon la contrainte opérationnelle.",
      valuePatternsLabel: "Modèles de valeur",
      valuePatternsHeadline: "La valeur se voit dans le travail qui change.",
      valuePatternsBody:
        "Les bons cas d’usage sont opérationnels, mesurables et proches des décisions récurrentes. Nous choisissons les processus où l’IA peut améliorer la vitesse, la qualité, le coût ou la précision sans retirer le jugement humain là où il compte.",
    },
    review: {
      eyebrow: "Examen décisionnel spécialisé",
      headline: "Vous devez prendre une décision réelle sur le financement, le lancement ou l’expansion d’une initiative d’IA?",
      body: "Vert AI examine une initiative en exploitation et détermine ce que les preuves soutiennent, ce qui demeure une hypothèse, ce qui pourrait échouer et les conditions à remplir avant de poursuivre. L’examen spécialisé peut aussi convenir à une initiative prête à faire l’objet d’une décision, mais pas encore en exploitation.",
      facts: [
        { label: "Périmètre", value: "Une initiative" },
        { label: "Délai standard", value: "10 jours ouvrables après la préparation des preuves" },
        { label: "Honoraires standard", value: "9 500 $ CA + taxes applicables" },
      ],
      applyLabel: "Présenter une demande",
      sampleLabel: "Voir l’exemple fictif",
    },
    operatingSystem: {
      eyebrow: "Du cadrage à la production",
      headline: "Choisir. Prouver. Déployer. Exploiter.",
      body: "Chaque phase réduit une incertitude précise : la valeur du processus, la qualité des preuves, la fiabilité du déploiement ou la capacité de l’équipe à exploiter le système.",
      compareEngagements: "Comparer les types de mandats",
      gatesLabel: "Garde-fous de production",
      methodLabel: "Méthode",
      methodHeadline: "Un parcours direct du cadrage au système en exploitation.",
      methodBody:
        "Le travail commence par le problème opérationnel, pas par le modèle. Chaque étape réduit le risque avant la production.",
      humanMarker: "HUMAIN /",
    },
    trust: {
      eyebrow: "Confiance",
      headline: "Un système d’IA en production exige plus qu’un modèle.",
      body: "Le système doit être observable, gouverné, mesurable et utilisable par les équipes qui en dépendent. Les personnes conservent l’autorité lorsque le jugement, le risque ou l’incidence sur la clientèle comptent.",
    },
    founder: {
      eyebrow: "Fondateur",
      headline: "Les décisions de production restent sous la responsabilité du fondateur.",
      body: "Guillaume dirige le cadrage et les décisions techniques clés. Son expérience professionnelle comprend des systèmes d’IA, d’apprentissage automatique et de données pour des environnements comme Louis Vuitton, Canada Goose, Lowe’s et Home Hardware. Ces entreprises ne sont pas présentées comme des clientes de Vert AI.",
      aboutLink: "Voir la livraison dirigée par le fondateur",
      insightsLink: "Lire l’analyse de mise en œuvre",
    },
    industries: {
      eyebrow: "Opérateurs",
      headline: "L’IA en production pour les entreprises où les opérations sont au cœur de l’activité.",
      body: "Vert AI présente une expertise sectorielle détaillée en commerce de détail et commerce électronique, en biens de consommation ainsi que dans les franchises et réseaux multisites. Les mêmes modèles de production peuvent s’appliquer à la fabrication, à la distribution et aux services professionnels et interentreprises lorsque le processus, les données, les contrôles et les responsabilités sont clairs.",
      exploreLink: "Explorer les contextes sectoriels",
      publishedLabel: "Guides sectoriels détaillés",
      publishedTitle: "Secteurs où Vert AI présente une expertise détaillée",
      publishedTypeLabel: "Guide détaillé",
      applicableLabel: "Modèles de production applicables",
      applicableTitle: "Autres contextes où les mêmes modèles de production s’appliquent",
      applicableBody: "Ces contextes mènent au modèle de solution pertinent. L’adéquation dépend d’un processus défini, de données utilisables, de contrôles explicites et d’une responsabilité claire.",
      applicableTypeLabel: "Modèle de solution",
    },
    fit: {
      eyebrow: "Adéquation",
      headline: "Un bon mandat commence par un problème opérationnel précis.",
      body: "Ces limites permettent de concentrer le cadrage sur les occasions qui peuvent devenir des capacités exploitées et mesurables.",
      goodFitLabel: "Bonne adéquation",
      notFitLabel: "Ne convient pas",
    },
    faqHeadline: "Questions fréquentes.",
    final: {
      eyebrow: "Prochaine décision",
      headline: "Présentez un projet d’IA en production qui mérite un cadrage sérieux.",
      body: "Apportez un processus, un problème de données ou une décision récurrente. Nous examinerons la valeur, le risque, la responsabilité et le chemin vers la production, y compris la possibilité de ne pas construire pour l’instant.",
    },
  },
  services: {
    breadcrumbs: { home: "Accueil", current: "Mandats" },
    schemaServiceType:
      "Systèmes d’IA en production, plateformes de données pour l’IA, intelligence décisionnelle et examen spécialisé de vérification de l’IA",
    flowStamp: "MANDATS / 03",
    choiceSituationLabel: "Situation",
    choiceEngagementLabel: "Mandat",
    review: {
      eyebrow: "Examen décisionnel spécialisé",
      headline: "Une décision importante mérite un examen fondé sur les preuves.",
      facts: [
        { label: "Périmètre", value: "Une initiative" },
        { label: "Délai standard", value: "10 jours ouvrables" },
        { label: "Début du délai", value: "Après la préparation des preuves" },
        { label: "Honoraires standard", value: "9 500 $ CA + taxes" },
      ],
      applyLabel: "Présenter une demande",
      sampleLabel: "Voir l’exemple fictif",
    },
  },
  industries: {
    breadcrumbs: { home: "Accueil", current: "Secteurs" },
    heroLabel: "Secteurs / Décisions opérationnelles",
    heroTitle: "Le secteur fournit le contexte. Le processus détermine le système.",
    heroSubtitle:
      "Vert AI présente son expertise sectorielle la plus détaillée dans le commerce de détail et le commerce électronique, les biens de consommation ainsi que les franchises et réseaux multisites. Les mêmes modèles de production peuvent s’appliquer à la fabrication, à la distribution et aux services professionnels et interentreprises lorsque le processus, les données, les exceptions et les responsabilités sont bien définis.",
    primaryCta: "Discuter d’un projet d’IA en production",
    secondaryCta: "Voir les formats de mandat",
    registryLabel: "Guides sectoriels détaillés",
    registryTitle: "Secteurs où Vert AI présente une expertise détaillée",
    registrySubtitle:
      "Chaque page précise les frictions, les données minimales, les contrôles humains, les mesures de référence et les conditions qui peuvent rendre un premier projet prématuré.",
    guideTypeLabel: "Guide détaillé",
    expansionLabel: "Modèles de production applicables",
    expansionTitle: "Autres contextes où les mêmes modèles de production s’appliquent",
    expansionSubtitle:
      "Les équipes de fabrication, de distribution et de services professionnels rencontrent souvent les mêmes enjeux de planification, de documents, de connaissances et d’exceptions. Ces liens mènent au modèle de système pertinent plutôt qu’à une offre sectorielle distincte.",
    patternTypeLabel: "Modèle de solution",
    fitStatement:
      "Le secteur n’est pas le critère décisif. Un bon mandat repose sur un processus à forte valeur, une personne responsable, des données utilisables, des limites de risque claires et un résultat opérationnel mesurable.",
    fitCta: "Présenter un processus précis",
    adjacentLabel: "Poursuivre l’évaluation",
    solutionsLink: "Comparer les solutions",
    servicesLink: "Voir les formats de mandat",
    ctaHeadline: "Présentez une décision récurrente, pas une liste de souhaits IA.",
    ctaDesc:
      "Apportez le processus actuel, les systèmes sources, le responsable de la décision et une mesure de référence. Vert AI évaluera si le premier investissement devrait viser les données, une aide à la décision ou un processus d’automatisation gouverné.",
    registryWord: "Registre",
    heroMetaSuffix: "guides sectoriels détaillés · 2 autres contextes opérationnels · Cadrage par décision",
    flowStamp: "SECTEURS / SYSTÈMES",
    endLabel: "FIN /",
    flowNodes: [
      { label: "Décision", sub: "Responsable" },
      { label: "Données", sub: "Signaux" },
      { label: "Système", sub: "Contrôles" },
      { label: "Exploitation", sub: "Référence" },
    ],
  },
  solutions: {
    breadcrumbs: { home: "Accueil", current: "Solutions" },
    flowNodes: [
      { label: "Problème", sub: "Cadrage" },
      { label: "Solution", sub: "Choix" },
      { label: "Mandat", sub: "Construction" },
      { label: "Production", sub: "Exploitation" },
    ],
    heroPatternsSuffix: "modèles de solution",
    heroCapabilitiesSuffix: "capacités complémentaires",
    registryLabel: "Registre",
    engagementPrefix: "Via ·",
    engagementLabels: {
      productionAiSystems: "Systèmes d’IA en production",
      decisionIntelligence: "Intelligence décisionnelle",
    },
    openPatternLabel: "Voir le modèle de solution",
    capabilityLabel: "Capacité",
    bridgeLinks: [
      { href: "/services", label: "Explorer les systèmes d’IA en production" },
      { href: "/industries", label: "Voir l’adéquation par secteur" },
    ],
    endLabel: "FIN /",
  },
  insights: {
    breadcrumbs: { home: "Accueil", current: "Perspectives" },
    flowNodes: [
      { label: "Question", sub: "Cadrage" },
      { label: "Cadre", sub: "Méthode" },
      { label: "Référence", sub: "Décision" },
    ],
    heroMetaSuffix: "articles · Cadres · Références opérationnelles",
    flowStamp: "PERSPECTIVES / GUIDES",
    featuredLabel: "À la une",
    readingPathsLabel: "Parcours de lecture",
    readingPathsTitle: "Des guides regroupés selon la décision à prendre.",
    readingPathsIntro:
      "Chaque parcours relie la stratégie, les données et les contrôles à une question de direction précise.",
    readingPaths: [
      {
        title: "Passer du pilote à l’exploitation",
        description:
          "Évaluation, gouvernance, supervision humaine et contrôles nécessaires pour confier un travail réel à un système d’IA.",
      },
      {
        title: "Choisir et financer la bonne initiative",
        description:
          "Cadres d’analyse pour choisir un processus, établir la valeur, comparer les options et exiger des preuves d’un fournisseur.",
      },
      {
        title: "Construire les fondations de données et de décision",
        description:
          "Architecture de données, définitions gouvernées et mécanismes qui transforment l’information en décision opérationnelle.",
      },
    ],
    continueLabel: "Poursuivre",
    adjacentLinks: [
      { href: "/solutions", label: "Voir les types de systèmes d’IA en production" },
      { href: "/contact", label: "Discuter d’un projet d’IA en production" },
    ],
    endLabel: "FIN /",
  },
  about: {
    breadcrumbs: { home: "Accueil", current: "À propos" },
    founderJobTitle: "Fondateur, Vert AI",
    positioningSignalsAria: "Principes de positionnement de Vert AI",
    positioningSignalsLabel: "Ce que cette page établit",
    nextStepLabel: "Prochaine étape",
  },
  work: {
    homeBreadcrumb: "Accueil",
    pathLabels: { contact: "CONSTRUIRE", verification: "VÉRIFIER" },
  },
} as const satisfies CopyShape<typeof enMarketingRouteCopy>;

export const marketingRouteCopy = {
  en: enMarketingRouteCopy,
  fr: frMarketingRouteCopy,
} as const satisfies Record<Locale, CopyShape<typeof enMarketingRouteCopy>>;
